import { useEffect, lazy, Suspense } from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { createBrandTheme } from "brand";
import LandingPage from "./pages/LandingPage";
import SheetsPage from "./pages/SheetsPage";
import OpenPage from "./pages/OpenPage";
import ScoreboardPage from "./pages/ScoreboardPage";
import ScoreboardErrorBoundary from "./components/scoreboard/ScoreboardErrorBoundary";
import ClassResultsPage from "./pages/ClassResultsPage";
import { initAnalytics, trackPageView } from "./analytics";

// #1638 — the whole authed /scoreboard/remote-control subtree (Cognito auth,
// common-fe app shell, score-common client) is lazy-loaded. AC2's board-bundle
// leak turned out to have TWO eager entry points into common-fe's single
// bundled dist module (packages/common-fe/dist/common-fe.js), not one:
// RemoteControlApp below (already lazy) plus this file's own root "common-fe"
// import for the badges. Rollup cannot split a module that is reachable
// EAGERLY from anywhere in the main entry, even if only one of two importers
// is eager, so the badges had to move to a lazy import too before the public
// board chunk actually stopped shipping react-oidc-context (see AC2 and the
// card's judge findings for the measured before/after).
const RemoteControlApp = lazy(() => import("./remote/RemoteControlApp"));
// #1700 — the rider portal no longer mounts the fixed-corner <UsaBadge />
// + <BuildBadge /> pair (bug #132: it floated on top of scrolling result
// cards on a narrow iOS viewport). BadgeFooter is the shared common-fe
// replacement, a real footer in normal document flow; it deliberately
// leaves the build number off (see BadgeFooter.jsx's header comment for the
// AC2/AC3 record) — logBuildId below covers "still available for
// support/debugging" instead.
const BadgeFooter = lazy(() => import("common-fe").then((m) => ({ default: m.BadgeFooter })));

function RemoteControlLoading() {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
      <CircularProgress />
    </Box>
  );
}

// Rider self-service score-sheet app (#621). Public pages:
//   /              landing — request your score sheets by email + CNO
//   /sheets/:token results — view/download generated score sheets
//   /open/:requestId/:rideId/:variant — #971 click-time presign
//     pass-through: View/Download on the sheets page link HERE (never a raw
//     S3 URL), and this page mints + redirects to a fresh presigned URL.
//   /scoreboard/:showId/:year[/:slug] — #1637 public scoreboard, full-bleed
//     and chrome-free (see isScoreboardRoute below), themed by its own view
//     theme rather than the brand theme every other route here uses.
//   /results/:showId/:year/:classNumber — #1641 public class-results page
//     (the QR-code target off the board's QRCorner), a normal
//     PageShell-chrome'd page (brand theme, badges) rather than a scoreboard
//     variant. #1854 renamed the param classId -> classNumber: the page
//     resolves classNumber -> classId (api.js's resolveClassId) THEN fetches
//     the unchanged, classId-keyed fetchClassResults.
// Brand theme wraps both so they're themed in light AND dark mode.
const theme = createBrandTheme();

// #948 — Google Analytics page views. This app is client-routed (react-router,
// no full page loads between routes), so a page_view has to be fired on every
// location change ourselves rather than relying on GA's default single load
// event. initAnalytics()/trackPageView() both no-op when GA isn't configured
// (see analytics.js) and never send the real path (which may contain a
// rider's private token) — only the route template.
//
// #1638 — this also fires for the authed /scoreboard/remote-control subtree,
// deliberately: trackPageView only ever sends location.pathname's route
// template (no query/hash/token), so there's no auth or PII leak here, same
// as the /sheets/:token route above. Kept on rather than special-cased off so
// remote-control usage shows up in the same GA property as the rest of the
// app.
function AnalyticsPageViews() {
  const location = useLocation();

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  return null;
}

// #1700 — the on-screen build stamp is gone from the rider portal (AC2), but
// the build id still needs to be reachable for support/debugging: log it to
// the console once on load. Dynamic import keeps common-fe out of the eager
// bundle, same reasoning as the BadgeFooter lazy import above (#1638).
function logBuildId() {
  import("common-fe").then((m) => {
    console.info(`Fox Village score-fe build: ${m.resolveBuildId()}`);
  });
}

export default function App() {
  const location = useLocation();
  // #1637 — the scoreboard is full-bleed and chrome-free by design (design
  // §4.1): the fixed-position badges every other route shows must not float
  // on top of a TV/projector board. #1638 — this also matches
  // /scoreboard/remote-control/*, deliberately: that subtree renders its own
  // AppFooter (RemoteShell), so the same badges would be redundant there too.
  const isScoreboardRoute = location.pathname.startsWith("/scoreboard/");

  // #1700 judge finding — logBuildId() dynamic-imports "common-fe" (see its
  // own header comment above), same module the BadgeFooter lazy import below
  // is gated off isScoreboardRoute for. Ungated, this effect ran on EVERY App
  // mount, including /scoreboard/* — the one subtree #1638 went to real
  // trouble to keep common-fe (and the react-oidc-context it pulls in) out
  // of entirely for the public board. Gating it the same way keeps that
  // holding: /scoreboard/* never fetches the common-fe chunk.
  useEffect(() => {
    if (!isScoreboardRoute) {
      logBuildId();
    }
  }, [isScoreboardRoute]);

  return (
    <ThemeProvider theme={theme} defaultMode="light">
      <CssBaseline />
      <AnalyticsPageViews />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/sheets/:token" element={<SheetsPage />} />
        <Route path="/open/:requestId/:rideId/:variant" element={<OpenPage />} />
        {/* #1638 — declared BEFORE the dynamic /scoreboard/:showId/:year
            routes below, on purpose: a 3-segment remote-control child path
            (e.g. /scoreboard/remote-control/sponsors) structurally collides
            with /scoreboard/:showId/:year (:showId="remote-control",
            :year="sponsors"). React Router v7 already ranks a static
            segment over a dynamic one regardless of declaration order, but
            this makes that precedence explicit rather than relying on it. */}
        <Route
          path="/scoreboard/remote-control/*"
          element={
            <Suspense fallback={<RemoteControlLoading />}>
              <RemoteControlApp />
            </Suspense>
          }
        />
        {/* #1703 — the second segment here is NOT always a year: it also
            serves the new short form /scoreboard/[usefCompId]/[slug]
            (AC2). Which reading applies is an explicit, documented rule
            (AC3, isBareFourDigitYear in score-api's scoreboard-public.js)
            — never left to this route's declaration order, which can't
            express the rule at all (both forms match the identical
            two-segment shape). ScoreboardPage forwards the raw segment to
            the API verbatim under its own param name either way; only the
            server decides which reading applies (see ScoreboardPage's own
            header comment for why that's not duplicated here). */}
        {/* #1798 AC2 — a LAST-RESORT boundary at the route, on top of the
            one ScoreboardPage.jsx already wraps its own render tree in.
            ScoreboardPage's own function body computes several derived
            values before it ever returns JSX (sponsorVariantForModeType,
            the classId/QR chain, the QR url's encodeURIComponent build) —
            a throw there happens before that inner boundary exists in the
            tree, so without an outer one it would still be a white screen.
            No `resetKey` here (unlike the inner boundary): this only ever
            catches a throw from ScoreboardPage's own body, which is a pure
            function of the unchanging route params, so there is nothing
            that would make a retry succeed short of a real navigation —
            React Router remounting this element on a route change already
            gives that for free. */}
        <Route
          path="/scoreboard/:showId/:year"
          element={
            <ScoreboardErrorBoundary>
              <ScoreboardPage />
            </ScoreboardErrorBoundary>
          }
        />
        <Route
          path="/scoreboard/:showId/:year/:slug"
          element={
            <ScoreboardErrorBoundary>
              <ScoreboardPage />
            </ScoreboardErrorBoundary>
          }
        />
        {/* #1854 — short, human-readable QR target: a class NUMBER (as
            printed on the daysheet), not the internal fvd: classId. This
            was renamed from :classId to :classNumber; ClassResultsPage now
            resolves classNumber -> classId (score-api's new public
            resolver) before calling the unchanged classId-keyed
            fetchClassResults. */}
        <Route path="/results/:showId/:year/:classNumber" element={<ClassResultsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {/* #1700 — a real footer in normal document flow, right after the
          routed page content. It can never overlap that content (it isn't
          fixed/absolute), unlike the old <UsaBadge />+<BuildBadge /> pair.
          Same isScoreboardRoute exclusion as before (full-bleed board). */}
      {!isScoreboardRoute && (
        <Suspense fallback={null}>
          <BadgeFooter />
        </Suspense>
      )}
    </ThemeProvider>
  );
}
